import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Bell, Calendar } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import AdminLayout from './AdminLayout';
import { Toaster, toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminAnnouncements = () => {
  const { token } = useAuth();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    text: '',
    link_url: '',
    link_text: '',
    is_active: false,
    background_color: '#F5A623',
    text_color: '#000000'
  });

  useEffect(() => {
    fetchAnnouncements();
  }, [token]);

  const fetchAnnouncements = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/announcements`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnnouncements(response.data);
    } catch (error) {
      console.error('Failed to fetch announcements:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingId) {
        await axios.put(`${API_URL}/api/admin/announcements/${editingId}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Announcement updated');
      } else {
        await axios.post(`${API_URL}/api/admin/announcements`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Announcement created');
      }
      
      setShowForm(false);
      setEditingId(null);
      setFormData({
        text: '',
        link_url: '',
        link_text: '',
        is_active: false,
        background_color: '#F5A623',
        text_color: '#000000'
      });
      fetchAnnouncements();
    } catch (error) {
      toast.error('Failed to save announcement');
    }
  };

  const handleEdit = (announcement) => {
    setFormData({
      text: announcement.text,
      link_url: announcement.link_url || '',
      link_text: announcement.link_text || '',
      is_active: announcement.is_active,
      background_color: announcement.background_color || '#F5A623',
      text_color: announcement.text_color || '#000000'
    });
    setEditingId(announcement.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this announcement?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/admin/announcements/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Announcement deleted');
      fetchAnnouncements();
    } catch (error) {
      toast.error('Failed to delete announcement');
    }
  };

  return (
    <AdminLayout>
      <Toaster position="top-right" richColors />
      
      <div data-testid="admin-announcements">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-2xl font-bold">Announcements</h1>
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center px-4 py-2 bg-primary text-primary-foreground font-medium rounded hover:brightness-110 transition-all"
            data-testid="add-announcement-btn"
          >
            <Plus className="w-5 h-5 mr-2" />
            Add Announcement
          </button>
        </div>

        {/* Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-card border border-border rounded-lg p-6 w-full max-w-lg">
              <h2 className="text-xl font-bold mb-6">
                {editingId ? 'Edit' : 'New'} Announcement
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Text *</label>
                  <textarea
                    value={formData.text}
                    onChange={(e) => setFormData({...formData, text: e.target.value})}
                    required
                    rows={3}
                    className="w-full px-4 py-3 bg-input border border-border rounded focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Link URL</label>
                    <input
                      type="url"
                      value={formData.link_url}
                      onChange={(e) => setFormData({...formData, link_url: e.target.value})}
                      className="w-full px-4 py-3 bg-input border border-border rounded focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Link Text</label>
                    <input
                      type="text"
                      value={formData.link_text}
                      onChange={(e) => setFormData({...formData, link_text: e.target.value})}
                      className="w-full px-4 py-3 bg-input border border-border rounded focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Background Color</label>
                    <input
                      type="color"
                      value={formData.background_color}
                      onChange={(e) => setFormData({...formData, background_color: e.target.value})}
                      className="w-full h-12 rounded border border-border cursor-pointer"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Text Color</label>
                    <input
                      type="color"
                      value={formData.text_color}
                      onChange={(e) => setFormData({...formData, text_color: e.target.value})}
                      className="w-full h-12 rounded border border-border cursor-pointer"
                    />
                  </div>
                </div>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">Active</span>
                </label>

                {/* Preview */}
                <div className="p-4 rounded" style={{ 
                  backgroundColor: formData.background_color,
                  color: formData.text_color
                }}>
                  <p className="text-sm font-medium">
                    {formData.text || 'Preview text will appear here'}
                    {formData.link_url && (
                      <span className="ml-2 underline">{formData.link_text || 'Learn More'}</span>
                    )}
                  </p>
                </div>

                <div className="flex gap-4 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowForm(false);
                      setEditingId(null);
                    }}
                    className="flex-1 px-4 py-2 border border-border rounded hover:bg-accent transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-primary text-primary-foreground font-medium rounded hover:brightness-110 transition-all"
                  >
                    {editingId ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Announcements List */}
        <div className="space-y-4">
          {loading ? (
            <div className="p-6 text-center text-muted-foreground">Loading...</div>
          ) : announcements.length === 0 ? (
            <div className="p-6 bg-card border border-border rounded-lg text-center text-muted-foreground">
              No announcements yet
            </div>
          ) : (
            announcements.map((announcement) => (
              <div 
                key={announcement.id}
                className="p-6 bg-card border border-border rounded-lg"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Bell className={`w-4 h-4 ${announcement.is_active ? 'text-green-500' : 'text-muted-foreground'}`} />
                      <span className={`text-xs font-medium ${
                        announcement.is_active ? 'text-green-500' : 'text-muted-foreground'
                      }`}>
                        {announcement.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div 
                      className="p-3 rounded text-sm"
                      style={{
                        backgroundColor: announcement.background_color,
                        color: announcement.text_color
                      }}
                    >
                      {announcement.text}
                      {announcement.link_url && (
                        <span className="ml-2 underline">{announcement.link_text || 'Learn More'}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleEdit(announcement)}
                      className="p-2 hover:bg-accent rounded transition-colors"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(announcement.id)}
                      className="p-2 hover:bg-destructive/10 hover:text-destructive rounded transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminAnnouncements;
